/**
 * London Smoke & Cure — Firestore-only order markers (not OrderDesk fields).
 * Safe to store on orders/{id}; strip before OrderDesk create/update payloads.
 */

import { stripUndefinedDeep } from './stripUndefinedDeep.js';

/** @param {object|null|undefined} order */
export function isOrderPlacedOnBehalf(order) {
    if (!order || typeof order !== 'object') return false;
    return order.placedOnBehalf === true || order.placedOnBehalf === 1 || order.placedOnBehalf === 'true';
}

/** Customer-facing label for Account order history (avoid internal “admin” wording). */
export function orderPlacedOnBehalfCustomerLabel(order) {
    if (!isOrderPlacedOnBehalf(order)) return '';
    return 'Placed by our team';
}

/**
 * Best-effort first name for Admin chip tooltip.
 * Prefers stored `placedOnBehalfByName`; else first segment of email local-part / bare value.
 * @param {object|null|undefined} order
 * @returns {string}
 */
export function orderPlacedOnBehalfAdminFirstName(order) {
    if (!order || typeof order !== 'object') return '';
    const stored = String(order.placedOnBehalfByName || '').trim();
    if (stored) return stored;

    const by = String(order.placedOnBehalfBy || '').trim();
    if (!by) return '';

    const local = by.includes('@') ? by.split('@')[0] : by;
    const token = local.split(/[._+\-]/).filter(Boolean)[0] || '';
    if (!token || token === 'admin') return '';
    return token.charAt(0).toUpperCase() + token.slice(1);
}

/**
 * Native title tooltip for Admin “Placed by admin” chip.
 * @param {object|null|undefined} order
 * @returns {string}
 */
export function orderPlacedOnBehalfAdminTooltip(order) {
    if (!isOrderPlacedOnBehalf(order)) return '';
    const first = orderPlacedOnBehalfAdminFirstName(order);
    if (first) return `Placed by ${first}`;
    return 'Placed by admin on behalf of the customer';
}

/**
 * Resolve a first name to store on place-on-behalf (profile preferred, then email local-part).
 * @param {{ firstName?: string, fullName?: string, displayName?: string, email?: string }|null|undefined} adminProfile
 * @param {{ email?: string, displayName?: string }|null|undefined} [authUser]
 * @returns {string}
 */
export function resolvePlacedOnBehalfAdminFirstName(adminProfile, authUser) {
    const fromProfile = String(adminProfile?.firstName || '').trim();
    if (fromProfile) return fromProfile;

    const fullName = String(adminProfile?.fullName || authUser?.displayName || '').trim();
    if (fullName) {
        const parts = fullName.split(/\s+/).filter(Boolean);
        if (parts[0]) return parts[0];
    }

    const email = String(adminProfile?.email || authUser?.email || '').trim();
    if (email.includes('@')) {
        const token = email.split('@')[0].split(/[._+\-]/).filter(Boolean)[0] || '';
        if (token && token !== 'admin') {
            return token.charAt(0).toUpperCase() + token.slice(1);
        }
    }
    return '';
}

/** Ops markers kept on Firestore; strip from OrderDesk payloads. */
export const FIRESTORE_ONLY_ORDER_PLACEMENT_KEYS = [
    'placedOnBehalf',
    'placedOnBehalfBy',
    'placedOnBehalfByName',
    'placedOnBehalfAt',
    'confirmationEmailMuted',
    'confirmationEmailStatus',
    'confirmationEmailError',
    'isTemporaryCustomer',
    'temporaryCustomerId',
    // SPA / Firestore mirrors — not OrderDesk order fields
    'pricingKey',
    'subtotal',
    'shippingReason',
    'collection',
    'reserved',
    'accountAdditionalEmailCopies',
    'DOCMatch_id',
    'createdAt',
    'lastUpdatedAt',
    'date_updated',
    // Submit and pay — Firestore markers only (OD uses payment_type / payment_status / checkout Payment)
    'payInAdvance',
    'paymentIntent',
];

/**
 * Shallow clone without Firestore-only keys for OrderDesk submit/sync.
 * Does not mutate the original (Firestore can keep the flags).
 * @param {object} order
 * @returns {object}
 */
export function stripFirestoreOnlyOrderFieldsForOrderDesk(order) {
    if (!order || typeof order !== 'object') return order;
    const next = { ...order };
    for (const key of FIRESTORE_ONLY_ORDER_PLACEMENT_KEYS) {
        delete next[key];
    }
    return next;
}

/**
 * Strip Firestore-only keys then deep-remove undefined (and FieldValue sentinels)
 * for OrderDesk PUT/POST bodies.
 * @param {object} order
 * @returns {object}
 */
export function prepareOrderPayloadForOrderDesk(order) {
    return (
        stripUndefinedDeep(stripFirestoreOnlyOrderFieldsForOrderDesk(order), {
            dropFieldValues: true,
        }) || {}
    );
}
